import { ConcourseDb } from '../ConcourseDb';
import { ActivityCodeDoc, TagData } from "common";


export class Tag {
    static getTagDb(projectId: string):ConcourseDb {
        return ConcourseDb.getInstance(`p-${projectId}-tags`);
    }

    static async getActivityCodes(projectId: string, onComplete:(schedules: ActivityCodeDoc[]) => void):Promise<ActivityCodeDoc[]> {
        const db = Tag.getTagDb(projectId);
        db.on('complete', (docs) => { onComplete(docs)})
        return await db.getDocs();
    }

    static async getActivityCodesSync(projectId: string):Promise<ActivityCodeDoc[]> {
      const db = Tag.getTagDb(projectId);
      return await db.getDocs();
    }

    static async getTags(projectId:string):Promise<TagData[]> {
      const db = Tag.getTagDb(projectId);
      return Tag.flattenActivityCodes(await db.getDocs());
    }

    static flattenActivityCodes(activityCodes: ActivityCodeDoc[]):TagData[] {
      return activityCodes.map((group) => group.values).flat();
    }

    static async saveActivityCode(projectId:string, activityCode:ActivityCodeDoc):Promise<ActivityCodeDoc[]> {
      const db = Tag.getTagDb(projectId);
      await db.saveDoc(activityCode);
      return await db.getDocs();
    }

    static getActivityCode(activityCodes: ActivityCodeDoc[], term: string): ActivityCodeDoc {
      return activityCodes.find((group) => {
        return group.name.toLowerCase() == term.toLowerCase()
      })
    }

    static getRootTags(activityCode:ActivityCodeDoc):TagData[] {
      return activityCode.values.filter(v => !v.parent_unique_id && !!v.unique_id);
    }

    static filterActivityCodes(activityCodes: ActivityCodeDoc[], term: string): ActivityCodeDoc[] {
      return activityCodes.filter((group) => {
        return group.name.toLowerCase().includes(term.toLowerCase())
      })
    }

    static filterTagsInActivityCodes(tagGroups: ActivityCodeDoc[], term: string): ActivityCodeDoc[] {
      const filtered:ActivityCodeDoc[] = []
      tagGroups.forEach((group) => {
        let groupTags = group.values || [];
        let tags:TagData[] = Tag.filterTags(groupTags, term);
        if (tags.length > 0 || group.name.toLowerCase().includes(term.toLowerCase())) {
          
          // take a copy of the array so we can add ancestors to it
          const tagsWithAncestors = [...tags];
          tags.forEach((tag) => {
            // find the ancestors for each tag
            const ancestors = Tag.getTagAncestors(tag, groupTags);
            ancestors.forEach((tag) => {
              // Only include the ancestor if it's not already in the list
              if (!tagsWithAncestors.find((t) => t.unique_id == tag.unique_id)) {
                tagsWithAncestors.push(tag);
              }
            });
          })
          filtered.push({...group, values:tagsWithAncestors});
        }
      });


      return filtered;
    }

    static getTagAncestors(tag:TagData, tags:TagData[]):TagData[] {
      const ancestors:TagData[] = [];
      let parent = tags.find((t) => t.unique_id!! && t.unique_id == tag.parent_unique_id);
      while(parent && !ancestors.find(a => a.unique_id == parent.unique_id)) {
        ancestors.push(parent);
        parent = tags.find((t) => t.unique_id!! && t.unique_id == parent.parent_unique_id);
      }
      return ancestors.reverse();
    }

    static getTagDecendents(tag:TagData, tags:TagData[]):TagData[] {
      const descendents:TagData[] = [];
      let children = tags.filter((t) => t.parent_unique_id == tag.unique_id);
      children.forEach(child => {
        descendents.push(...Tag.getTagDecendents(child, tags), child);
      });
      return descendents;
    }

    static filterTags(tags: TagData[], search: string): TagData[] {
        return tags.filter((tag) => {
            return tag.name.toLowerCase().includes(search.toLowerCase())
            || (tag.desription && tag.desription.toLowerCase().includes(search.toLowerCase()));
        });
    }

    static filterTagsByParent(tags: TagData[], search: string, parentUniqueId:number): TagData[] {
      return tags.filter((tag) => {
          return tag.name.toLowerCase().includes(search.toLowerCase()) && tag.parent_unique_id == parentUniqueId;
      });
    }

    static async getMaxUniqueIdFromTags(projectId: string): Promise<number> {
      const allTags = await Tag.getTags(projectId);
      if (allTags.length === 0) return null;
      const uniqueIds = allTags.map((doc: any) => doc.unique_id).filter(id => id !== undefined);
      if (uniqueIds.length === 0) return null;
      return Math.max(...uniqueIds);
    }

    static async getMaxUniqueIdFromActivityCodes(projectId: string): Promise<number> {
      const activityCodes = await Tag.getActivityCodesSync(projectId);
      if (activityCodes.length === 0) return null;
      const uniqueIds = activityCodes.map((doc: ActivityCodeDoc) => doc.unique_id).filter(id => id !== undefined);
      if (uniqueIds.length === 0) return null;
      return Math.max(...uniqueIds);
    }
}